import { Button } from '@ui/button.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { useTranslation } from 'react-i18next';
import { usePasswordUpdate } from '@r/api/generated/client.ts';
import { useRouter } from '@tanstack/react-router';
import { useUser } from '@r/api/auth.tsx';
import { Fragment, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { refreshWorkspaceData } from '@r/utils/refresh-workspace-data.ts';
import { PasswordInput } from '../ui/password-input.tsx';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert.tsx';
import { AlertCircle, Loader2 } from 'lucide-react';
import { PASSWORD_REGEX } from '@r/utils/regex/password.ts';

export function ChangePasswordForm() {
  const router = useRouter();
  const user = useUser();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const changePasswordSchema = z
    .object({
      password: z.string().regex(PASSWORD_REGEX, {
        message: t('form.password.invalid')
      }),
      passwordConfirmation: z.string().regex(PASSWORD_REGEX, {
        message: t('form.password.invalid')
      })
    })
    .refine((data) => data.password === data.passwordConfirmation, {
      path: ['passwordConfirmation'],
      message: t('auth.password.confirmation.invalid')
    });

  const form = useForm<z.infer<typeof changePasswordSchema>>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      password: '',
      passwordConfirmation: ''
    }
  });

  const activeWorkspace = user.data?.visibleWorkspaces?.find((workspace) => workspace.active);
  useEffect(() => {
    if (activeWorkspace && !activeWorkspace.passwordNeedToBeChanged) {
      router.invalidate();
    }
  }, [activeWorkspace?.passwordNeedToBeChanged]);

  const changePasswordMutation = usePasswordUpdate({
    mutation: {
      onSuccess: async () => {
        await refreshWorkspaceData(queryClient, router);
        changePasswordMutation.reset();
      }
    }
  });

  function onSubmit(data: z.infer<typeof changePasswordSchema>) {
    changePasswordMutation.mutate({ data: { newPassword: data.password } });
  }

  return (
    <div className="space-y-8">
      {changePasswordMutation.error ? (
        <Alert variant="destructive" className="mb-10">
          <AlertCircle className="w-4 h-4" />
          <AlertTitle>{t('auth.error.label')}</AlertTitle>
          <AlertDescription>{t('auth.error.description')} </AlertDescription>
        </Alert>
      ) : null}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
            control={form.control}
            name="password"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('auth.password.label')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder={t('auth.password.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="passwordConfirmation"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('auth.password.repeat.label')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder={t('auth.password.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button className="w-full" type="submit" disabled={changePasswordMutation.isPending}>
            {changePasswordMutation.isPending ? (
              <Fragment>
                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                {t('auth.save.modifications')}
              </Fragment>
            ) : (
              t('auth.save.modifications')
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}
